// Colour-code content categories with the extended brand accent range (see the
// design-system accent palette). Navy/red stay the primary voice; these tag
// categories so listings scan by topic. Categories are grouped into topic
// families, one accent each.
//
// IMPORTANT: the class strings below are LITERAL so Tailwind's JIT scanner
// generates them. Never build `text-brand-${x}` dynamically — it won't compile.

export type CategoryAccent = {
  /** tint background + readable text — pill tags / overlines */
  tag: string;
  /** solid accent fill + readable text — emphasis badges */
  solid: string;
  /** accent background — dots / rules */
  dot: string;
};

const ACCENTS = {
  teal: { tag: "bg-brand-teal/12 text-brand-teal", solid: "bg-brand-teal text-white", dot: "bg-brand-teal" },
  slate: { tag: "bg-brand-slate/12 text-brand-slate", solid: "bg-brand-slate text-white", dot: "bg-brand-slate" },
  coral: { tag: "bg-brand-coral/12 text-brand-coral", solid: "bg-brand-coral text-white", dot: "bg-brand-coral" },
  sand: { tag: "bg-brand-sand/25 text-brand-navy", solid: "bg-brand-sand text-brand-navy", dot: "bg-brand-sand" },
  forest: { tag: "bg-brand-forest/12 text-brand-forest", solid: "bg-brand-forest text-white", dot: "bg-brand-forest" },
  burgundy: { tag: "bg-brand-burgundy/12 text-brand-burgundy", solid: "bg-brand-burgundy text-white", dot: "bg-brand-burgundy" },
  gold: { tag: "bg-brand-gold/18 text-brand-navy", solid: "bg-brand-gold text-brand-navy", dot: "bg-brand-gold" },
} satisfies Record<string, CategoryAccent>;

const CATEGORY_TO_ACCENT: Record<string, keyof typeof ACCENTS> = {
  // Systems, data & infrastructure
  "system-design": "teal", database: "teal", sql: "teal", devops: "teal",
  // AI
  "artificial-intelligence": "slate",
  // Frontend & languages
  typescript: "coral", "html-css": "coral",
  // CS foundations
  algorithm: "sand", "data-structure": "sand", "design-pattern": "sand",
  // Craft & quality
  "clean-code": "forest", testing: "forest", accessibility: "forest",
  // Leadership & risk
  leadership: "burgundy", security: "burgundy",
  // Curated
  reviews: "gold",
};

export function categoryAccent(category?: string): CategoryAccent {
  const key = category ? CATEGORY_TO_ACCENT[category.toLowerCase()] : undefined;
  return ACCENTS[key ?? "slate"];
}

// Human display label for a category slug (fixes the ones title-casing gets wrong).
const LABEL_OVERRIDE: Record<string, string> = {
  typescript: "TypeScript",
  "html-css": "HTML / CSS",
  sql: "SQL",
  "artificial-intelligence": "Artificial Intelligence",
  "system-design": "System Design",
  devops: "DevOps",
  "clean-code": "Clean Code",
  "data-structure": "Data Structures",
  "design-pattern": "Design Patterns",
};

export function categoryLabel(category?: string): string {
  if (!category) return "";
  return LABEL_OVERRIDE[category.toLowerCase()] ?? category.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
